import { useMemo } from "react";
import { extractAirportIata } from "@/helpers/bookingHelpers";
import { FlightDetails, FlightStatus } from "@/redux/apis/clientFlight/types";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { swapDestinationForTerminal } from "@/redux/slices/clientBooking/clientBookingSlice";
import {
  selectDepartureFlightNumber,
  selectDestination,
  selectPickup,
  selectPickupDatetime,
  selectViewOnly,
} from "@/redux/slices/clientBooking/selectors";
import { legTime } from "../flightPickup";
import { useFlightDetails } from "./useFlightDetails";
import { useTerminalSwap } from "./useTerminalSwap";

/**
 * A departure flight the booking can still be timed off.
 *
 * A WHITELIST, matching `flightWillArrive` rather than inverting it. As an exclusion list this also
 * admitted `unknown` and `incident`, and `unknown` is the ORDINARY state of a flight more than about
 * a day out — the operator panel says so in as many words ("Flight details will be available 24 hours
 * before the flight"). Its legs come back null, so a drop-off booked two weeks ahead rendered an
 * empty card: em-dashes at both ends, no airports, no duration, no footer. The arrival side shows no
 * card at all in the same state, and should not differ.
 *
 * `landed` stays in: it is exactly what a departure leg reads once the aircraft is airborne, and a
 * booking whose flight has already gone is a data problem rather than a reason to hide its terminal.
 */
export const departureWillDepart = (flight?: FlightDetails | null): boolean =>
  !!flight && (["scheduled", "en-route", "landed"] as FlightStatus[]).includes(flight.status);

/**
 * Resolves the DEPARTURE flight for as long as the SHEET is open, and swaps the drop-off to the
 * terminal that flight leaves from (DEV-1487).
 *
 * The drop-off twin of {@link useSheetFlightPickup}, and mounted beside it in `ClientBooking` for the
 * same reason: `DateTimePanel` is where the departure lookup is consumed, but that panel only exists
 * while its sub-panel is open, and the terminal swap has to happen for a client who came to change
 * anything else. Holding the subscription here also means the panel's own lookup joins this cache
 * entry rather than firing a second request — which is why both build the argument the same way, from
 * `extractAirportIata(destination)` and the committed pickup's date.
 *
 * ## What it does NOT do
 *
 * It never touches the pickup TIME. The SUGGESTED PICKUP is a pre-fill the client can overwrite, so it
 * belongs to the panel that owns the time fields; committing it from here would move a booked time
 * behind a client who opened the sheet to change their notes. Contrast the arrival side, where the
 * engine will move the pickup anyway and the sheet's job is to keep the booking in step with it.
 */
export const useSheetDepartureFlight = () => {
  const dispatch = useAppDispatch();
  const pickup = useAppSelector(selectPickup);
  const destination = useAppSelector(selectDestination);
  const departureFlightNumber = useAppSelector(selectDepartureFlightNumber);
  const committed = useAppSelector(selectPickupDatetime);
  const viewOnly = useAppSelector(selectViewOnly);

  // The TRIP date — the day the client chose, read off the committed pickup. Unlike the arrival lookup
  // there is no wait to subtract, because nothing here derives the pickup from the flight.
  //
  // KNOWN GAP, and unavoidable from this side: the contract wants the day the flight DEPARTS (§1), and
  // the two part on a red-eye — a 00:30 departure booked from a 20:30 pickup the previous evening asks
  // Cirium about the wrong day, so the flight does not resolve and the drop-off keeps the airport-level
  // address. Closing it needs the departure day, which is only knowable from the lookup this date is
  // needed to make. The pickup's day is the best available guess and is right for every daytime flight.
  const departureDate = useMemo(() => {
    if (!committed) return null;
    const parsed = new Date(committed);
    if (isNaN(parsed.getTime())) return null;
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
  }, [committed]);

  const { data: flight, isError } = useFlightDetails({
    flightNumber: departureFlightNumber ?? "",
    date: departureDate ?? "",
    airport: extractAirportIata(destination) ?? undefined,
    direction: "departing",
    // Undefined rather than 0 for a missing pickup, so the server omits travel time instead of
    // measuring the drive from the Gulf of Guinea.
    pickupLat: pickup?.lat ?? undefined,
    pickupLng: pickup?.lng ?? undefined,
    skip: !departureFlightNumber || !departureDate || viewOnly,
  });

  const departureIso = legTime(flight?.departure);
  useTerminalSwap({
    // `isError` for the same reason the arrival swap weighs it: RTK Query retains the last good `data`
    // through a failed refetch, and rewriting the drop-off off a leg the server has since stopped
    // confirming is worse than leaving it.
    enabled: !viewOnly && !isError,
    legResolved: departureWillDepart(flight) && !!departureIso,
    flightNumber: departureFlightNumber,
    location: destination,
    leg: {
      iata: flight?.departure?.airport?.iata ?? null,
      name: flight?.departure?.airport?.name ?? null,
      terminal: flight?.departure?.terminal ?? null,
      city: flight?.departure?.airport?.city ?? null,
    },
    onSwap: (poi) =>
      dispatch(
        swapDestinationForTerminal({
          destination: poi,
          // Non-null by `legResolved` above — the swap never runs without a resolved departure.
          source: {
            flightNumber: departureFlightNumber ?? "",
            terminal: flight?.departure?.terminal ?? "",
            departureIso: departureIso ?? "",
          },
        })
      ),
  });
};
